import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import MenubarUser from "../../layouts/MenubarUser";
import { getOrders } from "../../function/users";
import { updateStatusOrder } from "../../function/admin";
import DownloadPopup from "./DownloadPopup";
import { Pagination } from "antd";
import {
  CloseCircleOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import "./OrderTracking.css";

const OrderTracking = () => {
  const { user } = useSelector((state) => ({ ...state }));
  const [orders, setOrders] = useState([]);
  const [downloadPopupVisible, setDownloadPopupVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 10;

  useEffect(() => {
    if (user && user.token) loadData();
  }, [user]);

  const loadData = () => {
    getOrders(user.token).then((res) => {
      const sorted = res.data.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setOrders(sorted);
    });
  };

  const handleCancelOrder = (order) => {
    if (order.orderstatus === "Completed") {
      toast.warning("คำสั่งซื้อที่เสร็จสมบูรณ์ไม่สามารถยกเลิกได้");
      return;
    }
    updateStatusOrder(user.token, order._id, "Cancelled")
      .then(() => {
        toast.success("ยกเลิกคำสั่งซื้อสำเร็จ");
        loadData();
      })
      .catch(() => toast.error("เกิดข้อผิดพลาดในการยกเลิกคำสั่งซื้อ"));
  };

  const handleOpenDownloadPopup = (order) => {
    setSelectedOrder(order);
    setDownloadPopupVisible(true);
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case "Processing":
        return { label: "กำลังดำเนินการ", className: "status-processing", icon: <SyncOutlined spin /> };
      case "Completed":
        return { label: "เสร็จสมบูรณ์", className: "status-completed", icon: <CheckCircleOutlined /> };
      case "Cancelled":
        return { label: "ยกเลิกแล้ว", className: "status-cancelled", icon: <CloseOutlined /> };
      default:
        return { label: status, className: "status-default", icon: <ClockCircleOutlined /> };
    }
  };

  const indexOfLast = currentPage * ordersPerPage;
  const indexOfFirst = indexOfLast - ordersPerPage;
  const currentOrders = orders.slice(indexOfFirst, indexOfLast);

  return (
    <div className="order-page">
      <div className="order-layout">
        {/* Sidebar */}
        <div className="order-sidebar">
          <MenubarUser />
        </div>

        {/* Main */}
        <div className="order-main">
          <div className="order-page-header">
            <h4 className="order-page-title">ติดตามคำสั่งซื้อ</h4>
            <span className="order-count-badge">{orders.length} รายการ</span>
          </div>

          {currentOrders.length === 0 ? (
            <div className="order-empty">
              <ClockCircleOutlined className="order-empty-icon" />
              <p>ยังไม่มีคำสั่งซื้อ</p>
            </div>
          ) : (
            <div className="order-list">
              {currentOrders.map((item, index) => {
                const statusConfig = getStatusConfig(item.orderstatus);
                return (
                  <div key={index} className="order-card">
                    {/* Card Header */}
                    <div className="order-card-header">
                      <div className="order-card-left">
                        <span className={`order-status-badge ${statusConfig.className}`}>
                          {statusConfig.icon}
                          {statusConfig.label}
                        </span>
                        <span className="order-number">
                          #{item.orderNumber || "ไม่มีหมายเลข"}
                        </span>
                      </div>
                      {item.orderstatus !== "Cancelled" && item.orderstatus !== "Completed" && (
                        <button
                          className="btn-cancel-order"
                          onClick={() => handleCancelOrder(item)}
                        >
                          <CloseCircleOutlined />
                          ยกเลิก
                        </button>
                      )}
                    </div>

                    {/* Products Table */}
                    <div className="order-table-wrap">
                      <table className="order-table">
                        <thead>
                          <tr>
                            <th>ชื่อสินค้า</th>
                            <th>ราคา</th>
                            <th>จำนวน</th>
                          </tr>
                        </thead>
                        <tbody>
                          {item.products.map((p, i) => (
                            <tr key={i}>
                              <td>{p.product?.title || "ไม่มีข้อมูลสินค้า"}</td>
                              <td>
                                {Number(p.price).toLocaleString("th-TH", {
                                  minimumFractionDigits: 2,
                                })}{" "}
                                บาท
                              </td>
                              <td>{p.count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Summary */}
                    <div className="order-summary">
                      {item.pointsDiscount > 0 && (
                        <div className="order-summary-row discount">
                          <span>ส่วนลดแต้มสะสม ({item.pointsUsed} แต้ม)</span>
                          <span>−{item.pointsDiscount} บาท</span>
                        </div>
                      )}
                      {item.couponDiscount > 0 && (
                        <div className="order-summary-row discount">
                          <span>คูปอง {item.couponCode || ""}</span>
                          <span>
                            −{Math.floor(item.cartTotal * (item.couponDiscount / 100))} บาท
                          </span>
                        </div>
                      )}
                      <div className="order-summary-row total">
                        <span>ยอดรวมสุทธิ</span>
                        <span>
                          {Number(item.cartTotal).toLocaleString("th-TH", {
                            minimumFractionDigits: 2,
                          })}{" "}
                          บาท
                        </span>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="order-card-footer">
                      <button
                        className={`btn-download ${item.orderstatus === "Completed" ? "" : "btn-download-disabled"}`}
                        onClick={() => {
                          if (item.orderstatus === "Completed") {
                            handleOpenDownloadPopup(item);
                          } else {
                            toast.warning("กรุณารอสถานะ Completed ก่อนดาวน์โหลด");
                          }
                        }}
                      >
                        <DownloadOutlined />
                        ดาวน์โหลดใบกำกับสินค้า
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="order-pagination">
            <Pagination
              current={currentPage}
              total={orders.length}
              pageSize={ordersPerPage}
              onChange={setCurrentPage}
            />
          </div>
        </div>
      </div>

      {downloadPopupVisible && selectedOrder && (
        <DownloadPopup
          visible={downloadPopupVisible}
          onClose={() => setDownloadPopupVisible(false)}
          order={selectedOrder}
        />
      )}
    </div>
  );
};

export default OrderTracking;